/**
 * Карточка проживания (manager v3 §5.2 «Детали проживания»). Открывается из
 * доски проживаний (StaysPage) и из шахматки номеров (RoomsChessboard) по
 * stay_id.
 *
 * Дизайн (manager v3 §5.2, app.lovarthotels.com/manager/stays/{id}):
 *   topbar = крошки «Проживания / {id}» + заголовок «{Гость} · номер {N}» +
 *   CTA-бар действий жизненного цикла; 2-колоночный grid (1280) → stacked (375):
 *     левый стек — card «Основной гость (booker)» (avatar + имя), card «Период»
 *     (даты → ночи, факт. заезд/выезд, статус-чипы);
 *     правая колонка — card «Счёт (folio)» (разбивка проживания + итог).
 *
 * Бэк: GET /v1/stays/{id} (projectStay) отдаёт state, период (from/to),
 * rate/total в копейках, currency, source, факт. заезд/выезд, причину. Имя
 * гостя и № номера приходят из контекста (calendar guest_name/room.number) —
 * модель stay их не хранит. Поля, которых нет на бэке (контакты, паспорт,
 * сопровождающие, дети, история заявок, позиции folio), НЕ выдумываем —
 * рендерим карточки дизайна только с реальными данными.
 */
import { useState } from 'react';
import { Button, Skeleton } from '@hsc/ui';
import type { StayState } from '@hsc/types';
import {
  STAY_STATE_LABEL,
  STAY_STATE_TONE,
  formatDate,
  formatMoney,
} from '../../lib/fleet-format.js';
import { useStay, useStayLifecycle } from './useStays.js';

/** Источник брони → русский лейбл (i18n-гейт). */
const SOURCE_LABEL: Readonly<Record<string, string>> = {
  direct: 'Прямое бронирование',
  manual: 'Заведено вручную',
  ota: 'Канал OTA',
  booking: 'Booking.com',
  import: 'Импорт',
  walk_in: 'Заселение без брони',
};

function sourceLabel(src: string): string {
  if (!src) return '—';
  return SOURCE_LABEL[src] ?? src;
}

/** Инициалы из имени гостя для avatar (1-2 буквы). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Кол-во ночей между двумя ISO-датами (целое, ≥0). */
function nights(from: string | undefined, to: string | undefined): number {
  if (!from || !to) return 0;
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const ms = b.getTime() - a.getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}

/** «N ночей» с русским склонением. */
function nightsLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'ночей';
  if (mod10 === 1 && mod100 !== 11) word = 'ночь';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'ночи';
  return `${n} ${word}`;
}

export interface StayDetailProps {
  stayId: string;
  canManage: boolean;
  onClose: () => void;
  /** № номера из контекста (calendar/list) — бэк stay не отдаёт. */
  roomNumber?: string | undefined;
  /** Имя гостя из контекста (calendar guest_name) — бэк stay не отдаёт. */
  guestName?: string | undefined;
}

export function StayDetail({
  stayId,
  canManage,
  onClose,
  roomNumber,
  guestName,
}: StayDetailProps): JSX.Element {
  const { data, isLoading, isError } = useStay(stayId);
  const lifecycle = useStayLifecycle();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const act = (action: 'confirm' | 'check-in' | 'check-out' | 'cancel', reason?: string): void => {
    lifecycle.mutate(
      { id: stayId, action, ...(reason !== undefined ? { reason } : {}) },
      { onSuccess: () => action === 'cancel' && setConfirmCancel(false) },
    );
  };

  const displayName = guestName && guestName.trim() !== '' ? guestName : 'Гость без имени';
  const stayShortId = stayId.length > 8 ? stayId.slice(0, 8) : stayId;

  return (
    <section
      className="stay-detail"
      aria-label="Карточка проживания"
      data-testid="stay-detail"
    >
      {/* topbar: крошки + заголовок + CTA-бар (manager v3 §5.2) */}
      <header className="stay-detail__top">
        <div className="stay-detail__head-text">
          <nav className="stay-detail__crumbs" aria-label="Хлебные крошки">
            Проживания / {stayShortId}
          </nav>
          <h2 className="stay-detail__title">
            {displayName}
            {(roomNumber ?? '') !== '' && (
              <span className="stay-detail__room" data-testid="stay-detail-room">
                {' '}
                · номер {roomNumber}
              </span>
            )}
          </h2>
        </div>

        <div className="stay-detail__cta">
          {canManage && data && (
            <div className="stay-detail__actions" data-testid="stay-detail-actions">
              {data.state === 'pending' && (
                <Button
                  size="sm"
                  onClick={() => act('confirm')}
                  loading={lifecycle.isPending}
                  data-testid="stay-action-confirm"
                >
                  Подтвердить
                </Button>
              )}
              {(data.state === 'pending' || data.state === 'confirmed') && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => act('check-in')}
                  loading={lifecycle.isPending}
                  data-testid="stay-action-checkin"
                >
                  Заезд
                </Button>
              )}
              {data.state === 'checked_in' && (
                <Button
                  size="sm"
                  onClick={() => act('check-out')}
                  loading={lifecycle.isPending}
                  data-testid="stay-action-checkout"
                >
                  Выезд
                </Button>
              )}
              {(data.state === 'pending' || data.state === 'confirmed') &&
                (confirmCancel ? (
                  <span className="stay-detail__confirm">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => act('cancel', 'manager cancel')}
                      loading={lifecycle.isPending}
                      data-testid="stay-action-cancel-yes"
                    >
                      Точно отменить
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmCancel(false)}>
                      Нет
                    </Button>
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmCancel(true)}
                    data-testid="stay-action-cancel"
                  >
                    Отменить
                  </Button>
                ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="stay-detail-close"
            aria-label="Назад к списку"
          >
            Назад
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="stay-detail__grid">
          <div className="stay-detail__col">
            <Skeleton height={92} radius={16} />
            <Skeleton height={120} radius={16} />
          </div>
          <Skeleton height={220} radius={16} />
        </div>
      ) : isError || !data ? (
        <p role="alert" className="stay-detail__err">
          Не удалось загрузить карточку проживания.
        </p>
      ) : (
        <div className="stay-detail__grid">
          {/* левый стек: гость + период */}
          <div className="stay-detail__col">
            <article className="stay-card" data-testid="stay-detail-guest">
              <h3 className="stay-card__title">Основной гость</h3>
              <div className="stay-card__guest">
                <span className="stay-card__avatar" aria-hidden="true">
                  {initials(displayName)}
                </span>
                <div className="stay-card__guest-text">
                  <strong className="stay-card__name">{displayName}</strong>
                  <span className="stay-card__guest-note">отвечает за счёт</span>
                </div>
              </div>
            </article>

            <article className="stay-card">
              <h3 className="stay-card__title">Период</h3>
              <p className="stay-card__period" data-testid="stay-detail-period">
                {formatDate(data.from)} → {formatDate(data.to)} ·{' '}
                <strong>{nightsLabel(nights(data.from, data.to))}</strong>
              </p>
              {(data.actual_check_in !== undefined || data.actual_check_out !== undefined) && (
                <p className="stay-card__eta">
                  {data.actual_check_in !== undefined && (
                    <>Заезд: {formatDate(data.actual_check_in)}</>
                  )}
                  {data.actual_check_in !== undefined && data.actual_check_out !== undefined && ' · '}
                  {data.actual_check_out !== undefined && (
                    <>Выезд: {formatDate(data.actual_check_out)}</>
                  )}
                </p>
              )}
              <div className="stay-card__chips">
                <span
                  className={`fleet-badge fleet-badge--${STAY_STATE_TONE[data.state] ?? 'muted'}`}
                  data-testid="stay-detail-state"
                >
                  {STAY_STATE_LABEL[data.state as StayState] ?? data.state}
                </span>
                <span className="fleet-badge">{sourceLabel(data.source)}</span>
              </div>
              {data.status_reason !== undefined && data.status_reason !== '' && (
                <p className="stay-card__reason">Причина: {data.status_reason}</p>
              )}
            </article>
          </div>

          {/* правая колонка: счёт (folio) */}
          <article className="stay-card stay-card--folio">
            <h3 className="stay-card__title">Счёт</h3>
            <table className="stay-folio">
              <thead>
                <tr>
                  <th>Услуга</th>
                  <th className="stay-folio__num">Ночей</th>
                  <th className="stay-folio__num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    Проживание
                    {(roomNumber ?? '') !== '' ? ` · номер ${roomNumber}` : ''}
                  </td>
                  <td className="stay-folio__num">{nights(data.from, data.to) || '—'}</td>
                  <td className="stay-folio__num">
                    {formatMoney(data.total_kopecks, data.currency)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="stay-folio__total">
              <span>Итого</span>
              <strong data-testid="stay-detail-total">
                {formatMoney(data.total_kopecks, data.currency)}
              </strong>
            </div>
            <p className="stay-folio__note">
              Тариф: {formatMoney(data.rate_kopecks, data.currency)} / ночь · оплата при выезде
            </p>
          </article>
        </div>
      )}

      {lifecycle.isError && (
        <p role="alert" className="stay-detail__err">
          Действие не выполнено. Возможно, недостаточно прав.
        </p>
      )}
    </section>
  );
}
